export const paths = {
  auth: {
    register: {
      path: "/register",
      getHref: (redirectTo?: string | null | undefined) =>
        `/register${redirectTo && redirectTo !== "/" ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ""}`,
    },
    login: {
      path: "/login",
      getHref: (redirectTo?: string | null | undefined) =>
        `/login${redirectTo && redirectTo !== "/" ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ""}`,
    },
    forgotPassword: {
      path: "/forgot-password",
      getHref: () => "/forgot-password",
    },
    resetPassword: {
      path: "/reset-password",
      getHref: (token?: string) =>
        `/reset-password${token ? `?token=${encodeURIComponent(token)}` : ""}`,
    },
    verifyEmail: {
      path: "/verify-email",
      getHref: (token?: string) =>
        `/verify-email${token ? `?token=${encodeURIComponent(token)}` : ""}`,
    },
  },

  home: {
    path: "/",
    getHref: () => "/",
  },

  app: {
    root: {
      path: "/",
      getHref: () => "/",
    },

    dashboard: {
      path: "dashboard",
      getHref: () => "/dashboard",
    },

    projects: {
      path: "projects",
      getHref: () => "/projects",
      detail: {
        path: ":id",
        getHref: (id: string) => `/projects/${id}`,
        tab: {
          getHref: (id: string, tab: string) => `/projects/${id}/${tab}`,
        },
      },
    },

    invoices: {
      path: "invoices",
      getHref: () => "/invoices",
      detail: {
        path: ":id",
        getHref: (id: string) => `/invoices/${id}`,
      },
    },

    clientInvoices: {
      path: "client-invoices",
      getHref: () => "/client-invoices",
      detail: {
        path: ":id",
        getHref: (id: string) => `/client-invoices/${id}`,
      },
    },

    paymentReports: {
      path: "payment-reports",
      getHref: () => "/payment-reports",
      detail: {
        path: ":id",
        getHref: (id: string, query?: string) =>
          `/payment-reports/${id}${query ? `?${query}` : ""}`,
      },
    },

    contractors: {
      path: "contractors",
      getHref: () => "/contractors",
      detail: {
        path: ":id",
        getHref: (id: string) => `/contractors/${id}`,
      },
    },

    documents: {
      path: "documents",
      getHref: () => "/documents",
      upload: {
        path: "upload",
        getHref: () => "/documents/upload",
      },
      detail: {
        path: ":id",
        getHref: (id: string) => `/documents/${id}`,
      },
    },

    dailyLogs: {
      path: "daily-logs",
      getHref: () => "/daily-logs",
      create: {
        path: "create",
        getHref: () => "/daily-logs/create",
      },
      detail: {
        path: ":id",
        getHref: (id: string) => `/daily-logs/${id}`,
      },
    },

    users: {
      path: "users",
      getHref: () => "/users",
      detail: {
        path: ":id",
        getHref: (id: string) => `/users/${id}`,
      },
    },

    purchaseOrders: {
      path: "purchase-orders",
      getHref: () => "/purchase-orders",
      create: {
        path: "create",
        getHref: () => "/purchase-orders/create",
      },
      detail: {
        path: ":id",
        getHref: (id: string) => `/purchase-orders/${id}`,
      },
    },

    clients: {
      path: "clients",
      getHref: () => "/clients",
      detail: {
        path: ":id",
        getHref: (id: string) => `/clients/${id}`,
      },
    },

    materials: {
      path: "materials",
      getHref: () => "/materials",
      detail: {
        path: ":id",
        getHref: (id: string) => `/materials/${id}`,
      },
    },

    suppliers: {
      path: "suppliers",
      getHref: () => "/suppliers",
      detail: {
        path: ":id",
        getHref: (id: string) => `/suppliers/${id}`,
      },
    },

    changeOrders: {
      path: "change-orders",
      getHref: () => "/change-orders",
      detail: {
        path: ":id",
        getHref: (id: string) => `/change-orders/${id}`,
      },
    },

    milestones: {
      path: "milestones",
      getHref: () => "/milestones",
      detail: {
        path: ":id",
        getHref: (id: string) => `/milestones/${id}`,
      },
    },

    punchlists: {
      path: "punchlists",
      getHref: () => "/punchlists",
      detail: {
        path: ":id",
        getHref: (id: string) => `/punchlists/${id}`,
      },
    },

    photos: {
      path: "photos",
      getHref: () => "/photos",
      detail: {
        path: ":id",
        getHref: (id: string) => `/photos/${id}`,
      },
    },

    notifications: {
      path: "notifications",
      getHref: () => "/notifications",
    },

    auditLogs: {
      path: "audit-logs",
      getHref: () => "/audit-logs",
      detail: {
        path: ":id",
        getHref: (id: string) => `/audit-logs/${id}`,
      },
    },

    reports: {
      path: "reports",
      getHref: () => "/reports",
      financial: {
        path: "financial",
        getHref: () => "/reports/financial",
      },
      projects: {
        path: "projects",
        getHref: () => "/reports/projects",
      },
      timeTracking: {
        path: "time-tracking",
        getHref: () => "/reports/time-tracking",
      },
    },

    settings: {
      path: "settings",
      getHref: () => "/settings",
      profile: {
        path: "profile",
        getHref: () => "/settings/profile",
      },
      preferences: {
        path: "preferences",
        getHref: () => "/settings/preferences",
      },
      company: {
        path: "company",
        getHref: () => "/settings/company",
      },
      notifications: {
        path: "notifications",
        getHref: () => "/settings/notifications",
      },
    },

    portal: {
      path: "portal",
      getHref: () => "/portal",
      magicLink: {
        path: "magic-link",
        getHref: (token?: string) =>
          `/portal/magic-link${token ? `?token=${encodeURIComponent(token)}` : ""}`,
      },
      project: {
        path: "project/:projectId",
        getHref: (id: string) => `/portal/project/${id}`,
        overview: {
          getHref: (id: string) => `/portal/project/${id}`,
        },
        progress: {
          path: "progress",
          getHref: (id: string) => `/portal/project/${id}/progress`,
        },
        materials: {
          path: "materials",
          getHref: (id: string) => `/portal/project/${id}/materials`,
        },
        payments: {
          path: "payments",
          getHref: (id: string) => `/portal/project/${id}/payments`,
        },
        decisions: {
          path: "decisions",
          getHref: (id: string) => `/portal/project/${id}/decisions`,
        },
        punchlists: {
          path: "punchlists",
          getHref: (id: string) => `/portal/project/${id}/punchlists`,
          detail: {
            path: ":itemId",
            getHref: (projectId: string, itemId: string) =>
              `/portal/project/${projectId}/punchlists/${itemId}`,
          },
        },
        changeOrders: {
          path: "change-orders",
          getHref: (id: string) => `/portal/project/${id}/change-orders`,
          detail: {
            path: ":id",
            getHref: (projectId: string, id: string) =>
              `/portal/project/${projectId}/change-orders/${id}`,
          },
        },
        milestones: {
          path: "milestones/:id",
          getHref: (projectId: string, id: string) =>
            `/portal/project/${projectId}/milestones/${id}`,
        },
        invoices: {
          path: "invoices/:id",
          getHref: (projectId: string, id: string) =>
            `/portal/project/${projectId}/invoices/${id}`,
        },
        dailyLogs: {
          path: "daily-logs/:id",
          getHref: (projectId: string, id: string) =>
            `/portal/project/${projectId}/daily-logs/${id}`,
        },
        documents: {
          path: "documents/:id",
          getHref: (projectId: string, id: string) =>
            `/portal/project/${projectId}/documents/${id}`,
        },
      },
      invoices: {
        path: "invoices",
        getHref: () => "/portal/invoices",
        detail: {
          path: ":id",
          getHref: (id: string) => `/portal/invoices/${id}`,
        },
      },
    },
  },
} as const;
